import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { VideoMetadataInput } from "@vedaunion/shared";
import { ApiError, apiRequest } from "../../../lib/api";
import {
  useInvalidateAfterUpload,
  useUploadInit,
} from "../../../lib/youtube";
import { VideoMetadataForm } from "../../../components/youtube/VideoMetadataForm";
import { UploadProgressBar } from "../../../components/youtube/UploadProgress";
import {
  formatBytes,
  startUpload,
  UploadError,
  type UploadHandle,
  type UploadProgress,
} from "../../../components/youtube/UploadEngine";

/**
 * Two-section upload page:
 *
 *   1. Drop zone → file picker. On valid pick, the metadata form appears
 *      with the filename pre-seeded as the title.
 *   2. Editor fills in metadata. **Save as private** kicks off the upload:
 *      first init (server pre-flights quota + initiates the YouTube
 *      session), then a streaming POST of the file bytes to our server's
 *      `/upload/bytes/:uploadId` (the server proxies them to YouTube,
 *      which is necessary because YouTube's session URL doesn't allow
 *      browser cross-origin PUTs). Progress bar updates via XHR upload
 *      events. On success, navigate to the edit page.
 *
 * v1 limitation: no resume across tab close. If the upload fails, the
 * user re-picks the file and starts over.
 */

const ACCEPT_MIME = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-matroska",
];
const MAX_BYTES = 5 * 1024 * 1024 * 1024;

interface ActiveUpload {
  status: "uploading" | "complete" | "error";
  videoId: string | null;
  progress: UploadProgress | null;
  errorMessage: string | null;
}

export default function YouTubeUpload() {
  const navigate = useNavigate();
  const initMutation = useUploadInit();
  const invalidateAfterUpload = useInvalidateAfterUpload();

  const [file, setFile] = useState<File | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [active, setActive] = useState<ActiveUpload | null>(null);
  const [thumbBlob, setThumbBlob] = useState<Blob | null>(null);
  const [topError, setTopError] = useState<string | null>(null);
  const handleRef = useRef<UploadHandle | null>(null);

  const onPickFile = (picked: File) => {
    setPickError(null);
    setTopError(null);
    if (!ACCEPT_MIME.includes(picked.type)) {
      setPickError(
        "That file isn't a video we can upload. We accept MP4, WebM, MOV, AVI, and MKV.",
      );
      return;
    }
    if (picked.size > MAX_BYTES) {
      setPickError(
        `File is ${formatBytes(picked.size)}; the cap is 5 GB. Compress or trim before re-trying.`,
      );
      return;
    }
    setFile(picked);
  };

  const cancelUpload = () => {
    handleRef.current?.abort();
    handleRef.current = null;
    setActive(null);
    setFile(null);
    setThumbBlob(null);
    setTopError(null);
  };

  const onSave = async (values: VideoMetadataInput) => {
    if (!file) {
      setTopError("Pick a video file first.");
      return;
    }
    setTopError(null);
    setActive({
      status: "uploading",
      videoId: null,
      progress: null,
      errorMessage: null,
    });

    let uploadId: string;
    try {
      const init = await initMutation.mutateAsync({
        metadata: values,
        contentType: file.type,
        contentLength: file.size,
      });
      uploadId = init.uploadId;
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Couldn't start upload.";
      setActive({ status: "error", videoId: null, progress: null, errorMessage: msg });
      setTopError(msg);
      return;
    }

    const handle = startUpload({
      uploadUrl: `/api/youtube/upload/bytes/${uploadId}`,
      file,
      contentType: file.type,
      totalBytes: file.size,
      onProgress: (p) =>
        setActive((s) => (s ? { ...s, progress: p } : s)),
    });
    handleRef.current = handle;

    let videoId: string;
    try {
      videoId = await handle.promise;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed.";
      const userAborted =
        err instanceof UploadError && err.userAborted;
      if (userAborted) {
        // cancelUpload() already reset state; nothing to do.
        return;
      }
      setActive({
        status: "error",
        videoId: null,
        progress: null,
        errorMessage: msg,
      });
      setTopError(msg);
      return;
    }

    // Mark complete in the UI; if there's a thumbnail, push it now.
    setActive({
      status: "complete",
      videoId,
      progress: {
        bytesUploaded: file.size,
        totalBytes: file.size,
        ratio: 1,
        speedBps: 0,
        etaSec: 0,
      },
      errorMessage: null,
    });

    if (thumbBlob) {
      try {
        const res = await apiRequest(`/api/youtube/videos/${videoId}/thumbnail`, {
          method: "POST",
          headers: { "Content-Type": thumbBlob.type || "image/jpeg" },
          body: thumbBlob,
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          // Non-fatal — we still have the video, just no custom thumbnail.
          console.warn("[upload] thumbnail set failed:", body.error ?? res.status);
        }
      } catch (e) {
        console.warn("[upload] thumbnail set network error:", e);
      }
    }

    invalidateAfterUpload();
    navigate(`/dashboard/youtube/videos/${videoId}?just=uploaded`);
  };

  const submitting = active?.status === "uploading" || initMutation.isPending;

  return (
    <div className="yt-upload-page">
      {!file ? (
        <DropZone onPick={onPickFile} error={pickError} />
      ) : (
        <div className="yt-upload-grid">
          <aside className="yt-upload-aside">
            {active ? (
              <>
                <UploadProgressBar
                  progress={active.progress}
                  fileName={file.name}
                  fileSize={file.size}
                  status={active.status}
                />
                {active.status === "error" && active.errorMessage && (
                  <p className="yt-admin-error mt-3">{active.errorMessage}</p>
                )}
              </>
            ) : (
              <div>
                <p className="page-eyebrow mb-2">Selected file</p>
                <p className="font-grotesk text-[0.85rem] break-words">
                  {file.name}
                </p>
                <p className="font-grotesk text-[0.7rem] text-[var(--color-ink-mute)] mt-1">
                  {formatBytes(file.size)} · {file.type}
                </p>
              </div>
            )}
            <div className="yt-admin-actions mt-3">
              <button
                type="button"
                className="btn-app-ghost is-danger"
                onClick={cancelUpload}
              >
                {active ? "Cancel upload" : "Pick a different file"}
              </button>
            </div>
          </aside>
          <div className="yt-upload-form">
            <VideoMetadataForm
              defaultValues={{
                title: file.name.replace(/\.[^/.]+$/, "").slice(0, 100),
                privacyStatus: "private",
              }}
              onThumbnailChange={setThumbBlob}
              onSubmit={onSave}
              submitLabel="Upload to YouTube ({{privacy}})"
              submittingLabel="Uploading…"
              submitting={submitting}
              topError={topError}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function DropZone({
  onPick,
  error,
}: {
  onPick: (file: File) => void;
  error: string | null;
}) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      className={`yt-dropzone ${dragOver ? "is-over" : ""} ${error ? "is-error" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onPick(file);
      }}
    >
      <p className="yt-dropzone-headline font-display-italic">
        Drop a video here, or
      </p>
      <button
        type="button"
        className="btn-app yt-dropzone-button"
        onClick={() => inputRef.current?.click()}
      >
        Choose a file
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_MIME.join(",")}
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPick(file);
          e.target.value = "";
        }}
      />
      <p className="yt-dropzone-hint font-grotesk">
        MP4 · WebM · MOV · AVI · MKV — up to 5 GB. Test uploads stay
        private until reviewed.
      </p>
      {error && (
        <p className="yt-admin-error mt-3" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
